(ns graph-ui.web.responsive
  "WHAT IS THIS SCREEN? — answered once for the whole client, in two parts: the
   narrow question (PLAN-ikc-v3-015-01 phase 6) and the display profile
   (ADR-graph-ui-v0-021-01, PLAN-ikc-v3-017-01 phase 0). The second is derived
   from the first, so there is one media query here and one number in themes.edn.

   George reads a conversation on a Samsung Galaxy S26 Ultra, in portrait, with
   one hand, while he walks the dog. A phone in portrait is about 360 CSS
   pixels wide. The reader's split pane opens at 270 and keeps 280 for the
   filling pane, so the island was 556 pixels wide inside a 360-pixel window
   and scrolled sideways under his thumb.

   THE NUMBER IS DECLARED, NOT TYPED HERE. `--viewport-narrow` is a token in
   dsl/instances/themes.edn, written onto the document root by
   graph-ui.web.theme/apply-theme! like every other token, so the breakpoint and
   the design system move together and two nodes can never disagree about it.
   This namespace reads that one property and nothing else. NO STYLESHEET ASKS
   THE WIDTH QUESTION, deliberately: a CSS media query cannot read a custom
   property, so the number would have had to exist twice. The two rules
   css/ikc-base.css does carry ask `pointer: coarse` instead — is there a
   finger — which needs no number. A rule that must follow the WIDTH reads
   the `data-narrow` attribute that set-narrow! writes on the root instead
   (the right gutter's fold, lane PH, 2026-09-29).

   ONE LISTENER FOR THE WINDOW. `narrow?` is a reagent read, so any component
   that derefs it re-renders when the window crosses the breakpoint — the same
   reagent-atom pattern graph-ui.web.xray and graph-ui.web.ruler already use for
   a fact about the frame rather than about anything on the canvas."
  (:require [reagent.core :as r]
            [clojure.string :as string]
            [graph-ui.web.generated-themes :as gen]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @adr ADR-graph-ui-v0-011-01-model-declared-sizing
;; @adr ADR-graph-ui-v0-009-01-rich-theming-as-data
;; @adr ADR-graph-ui-v0-021-01-the-display-profile
;; CONCEPT: CONCEPT-graph-ui-v0-001-01-three-graphs

(def token-property
  "The declared token this namespace reads. Named once, so the token gate can
   see that something outside the mirror consumes it."
  "--viewport-narrow")

(def fallback-breakpoint
  "What to use when the property is not on the root yet. The first paint runs
   before apply-theme!, and a client with no breakpoint at all would treat a
   phone as a desktop — the silent version of the defect this closes. 48rem is
   themes.edn's own value."
  "48rem")

(defonce ^:private narrow-state (r/atom false))
(defonce ^:private installed (atom nil))

(defn media-query
  "A max-width media query for `breakpoint`. Pure, and the only place the query
   is spelled: `\"48rem\"` -> `\"(max-width: 48rem)\"`. A blank or missing value
   yields nil rather than the string `(max-width: )`, which every browser
   discards while reporting nothing."
  [breakpoint]
  (let [b (some-> breakpoint str string/trim not-empty)]
    (when b (str "(max-width: " b ")"))))

(defn declared-breakpoint
  "The breakpoint as the document root declares it, falling back to
   `fallback-breakpoint`. Read at install time, not cached at load time: the
   theme writes the property after this namespace is loaded."
  []
  (if (exists? js/document)
    (-> (js/getComputedStyle (.-documentElement js/document))
        (.getPropertyValue token-property)
        str
        string/trim
        not-empty
        (or fallback-breakpoint))
    fallback-breakpoint))

(defn narrow?
  "Reactive read: is the window narrower than the declared breakpoint? False
   until install! runs, and false in any environment with no matchMedia — a
   node test and the TUI both have one width and no phone."
  []
  @narrow-state)

(def narrow-attribute
  "The attribute the document root carries while the window is narrow. A
   stylesheet reads THIS, not the width: a CSS media query cannot read
   --viewport-narrow, so a rule that asked the width would hold the number a
   second time. css/ikc-base.css folds the right gutter under the text with it
   (SPEC-alatus-v0-002-02 row C-61, lane PH)."
  "data-narrow")

(defn mark-root!
  "Write the narrow answer onto `root`: the attribute while narrow, no attribute
   while wide. So a wide window has the DOM it had before this existed. Returns
   the answer it wrote. `root` is the document element in a browser, and any
   object with setAttribute and removeAttribute in a test."
  [root v]
  (let [v (boolean v)]
    (when root
      (if v
        (.setAttribute root narrow-attribute "")
        (.removeAttribute root narrow-attribute)))
    v))

(defn set-narrow!
  "The ONE writer of the narrow state. install!'s media listener calls it, and a
   test calls it to drive the whole tree through the breakpoint — a node runner
   has no window and no matchMedia, so without this the narrow render could only
   be checked in a browser, which is how un-rendered layout code shipped before
   (ADR-graph-ui-v0-011-01's safety net).

   IT ALSO MARKS THE DOCUMENT ROOT (mark-root!), so the reagent state and the
   stylesheet can never disagree about the window. Returns the new value."
  [v]
  (when (exists? js/document)
    (mark-root! (.-documentElement js/document) v))
  (reset! narrow-state (boolean v)))

(defn install!
  "Attach the one media-query listener. Idempotent: a second call re-reads the
   declared breakpoint and replaces the listener, which is what a theme change
   needs. Returns the query it installed, or nil where matchMedia is absent."
  []
  (when (and (exists? js/window) (.-matchMedia js/window))
    (when-let [q (media-query (declared-breakpoint))]
      (let [mql (.matchMedia js/window q)
            on  (fn [_] (set-narrow! (.-matches mql)))]
        (when-let [{:keys [prev handler]} @installed]
          (.removeEventListener prev "change" handler))
        (.addEventListener mql "change" on)
        (reset! installed {:prev mql :handler on})
        (set-narrow! (.-matches mql))
        q))))

;; ---------------------------------------------------------------------------
;; THE DISPLAY PROFILE (ADR-graph-ui-v0-021-01, PLAN-ikc-v3-017-01 phase 0)
;;
;; The boolean above answers ONE question about the screen, and the reading pane
;; needs three: how many tiles fit, is motion allowed, may an answer stream. A
;; profile is a named row of those capabilities, declared in
;; dsl/instances/themes.edn and mirrored here through generated-themes.
;;
;; NO SECOND WIDTH QUESTION IS ASKED. A row says :when :narrow or :when
;; :not-narrow, and both are answered by `narrow?` above — the one media query
;; this namespace already installs. So a boundary pixel cannot make the profile
;; and a node's :narrow slot disagree, and the number still exists once, in
;; themes.edn.
;;
;; :chosen IS NEVER DERIVED. No browser reports an e-ink panel, so the still
;; profile is selected by a person and by nothing else.
;; ---------------------------------------------------------------------------

(def declared-profiles
  "The rows, as themes.edn declares them. Read from the committed mirror, so this
   namespace holds no capability of its own."
  (:profiles gen/theme-data))

(defonce ^:private chosen-state (r/atom nil))

(defn profile-for
  "PURE: which row is in force. `rows` is the declaration, `chosen` is the
   person's selection or nil, `narrow-now?` is the window's one answer.

   A chosen row wins, INCLUDING over the width — that is the whole point of a
   choice. A chosen key the declaration does not hold is ignored rather than
   honoured, so a stale localStorage value cannot leave the client with no
   profile. Returns the row's key, or nil when the rows cannot answer."
  [rows chosen narrow-now?]
  (if (contains? rows chosen)
    chosen
    (let [want (if narrow-now? :narrow :not-narrow)]
      (some (fn [[k row]] (when (= want (:when row)) k)) rows))))

(defn profile
  "Reactive read: the key of the row in force right now."
  []
  (profile-for declared-profiles @chosen-state (narrow?)))

(defn set-profile!
  "The ONE writer of the person's choice. `nil` returns the client to the derived
   answer. A key the declaration does not hold is refused and returns nil, so a
   typo or a stale stored value cannot select a screen that was never declared.
   Returns the key it stored."
  [k]
  (cond
    (nil? k)                        (reset! chosen-state nil)
    (contains? declared-profiles k) (reset! chosen-state k)
    :else nil))

(defn capability
  "PURE: one capability of one row, with the value to use when nothing declares
   it. An absent declaration gives TODAY'S behaviour and never more room than a
   row grants: one tile, motion on, streaming on. So a missing row cannot quietly
   produce a six-tile grid."
  [rows k slot fallback]
  (let [v (get-in rows [k slot])]
    (if (nil? v) fallback v)))

(defn max-tiles
  "Reactive read: how many tiles the pane may hold. One, until a row says more."
  []
  (capability declared-profiles (profile) :max-tiles 1))

(defn motion?
  "Reactive read: may the surface animate."
  []
  (true? (capability declared-profiles (profile) :motion true)))

(defn stream?
  "Reactive read: may an answer arrive token by token."
  []
  (true? (capability declared-profiles (profile) :stream true)))

(defn profile-label
  "What a person reads for a row. The row's own :label, or the key."
  [k]
  (or (get-in declared-profiles [k :label]) (some-> k name)))

;; ---------------------------------------------------------------------------
;; The :narrow universal slot (dsl/contexts/ui.edn v2.0-SNAPSHOT)
;; ---------------------------------------------------------------------------

(defn hidden?
  "Does the declared :narrow value mean this node is ABSENT right now? Pure —
   `narrow` is the slot's value and `narrow-now?` is the window's state, passed
   in rather than read, so the rule is testable with no window at all."
  [narrow narrow-now?]
  (boolean (and narrow-now? (= :hide narrow))))

(defn scroll-class
  "The class a :narrow :scroll node carries while the window is narrow: its own
   sideways scroll box, so the PAGE keeps one width. Pure. nil when the value
   asks for nothing, so the ordinary render stays byte-identical."
  [narrow narrow-now?]
  (when (and narrow-now? (= :scroll narrow))
    "overflow-x-auto max-w-full"))

(defn split-narrow
  "What a :split-pane does right now: :keep, :fold or :stack. Pure.

   A value the split does not honour (:hide, :scroll) falls back to :keep
   rather than to the default, because :hide on a split is a description saying
   something this kind cannot do, and quietly folding instead would be the
   renderer inventing an answer — which is the whole of
   ADR-graph-ui-v0-011-01."
  [narrow narrow-now?]
  (if (and narrow-now? (contains? #{:fold :stack} narrow)) narrow :keep))
